import { menus, MenuObject } from "@/constants/menu";
import { useCurrentUserRole } from "@/features/auth/hooks/useCurrentUserRole";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { background, text } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { hexToRgba } from "@/utils/pieces";
import { Drawer, Stack, Tooltip, Typography, styled } from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import zIndex from "@mui/material/styles/zIndex";
import { FC, useMemo } from "react";
import { matchRoutes, useLocation, useNavigate } from "react-router-dom";

type TAppDrawerProps = {
  isOpenDrawer: boolean;
  toggleDrawer: () => void;
};

export const AppDrawer: FC<TAppDrawerProps> = ({ isOpenDrawer, toggleDrawer }) => {
  const { isLaptop } = useMediaQuery();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { role, isLoading: isRoleLoading } = useCurrentUserRole();

  const visibleMenus = useMemo(
    () => menus.filter((item) => !item.allowRoles || (!isRoleLoading && !!role && item.allowRoles.includes(role))),
    [role, isRoleLoading],
  );

  const sections = useMemo(() => {
    const order: string[] = [];
    const grouped = new Map<string, MenuObject[]>();
    visibleMenus.forEach((item) => {
      const key = item.section ?? "";
      if (!grouped.has(key)) {
        grouped.set(key, []);
        order.push(key);
      }
      grouped.get(key)!.push(item);
    });
    return order.map((key) => ({ label: key, items: grouped.get(key)! }));
  }, [visibleMenus]);

  const DrawerContent = useMemo(() => {
    const renderItem = (item: MenuObject) => {
      const isActive = item.relatedRoutes?.some((route) => Boolean(matchRoutes([{ path: route }], pathname)));
      if (isLaptop)
        return (
          <Tooltip title={item.text} placement="right" key={item.text}>
            <ListItemWrapper isLaptop={isLaptop} className={isActive ? "active" : ""} onClick={() => navigate(item.path!)}>
              <Stack flexDirection="row" justifyContent="center" alignItems="center">
                {item.icon && <item.icon width={24} height={24} />}
              </Stack>
            </ListItemWrapper>
          </Tooltip>
        );
      return (
        <ListItemWrapper isLaptop={isLaptop} key={item.text} className={isActive ? "active" : ""} onClick={() => navigate(item.path!)}>
          <Stack flexDirection="row" alignItems="center" gap={1.5}>
            {item.icon && <item.icon width={24} height={24} />}
            <Typography variant="body14Bold">{item.text}</Typography>
          </Stack>
        </ListItemWrapper>
      );
    };

    return (
      <ListWrapper isLaptop={isLaptop} gap={1}>
        {sections.map((section) => (
          <Stack key={section.label || "_root"} gap={1}>
            {section.label && (
              <>
                <Divider color={background.white} sx={{ opacity: 0.3, my: 0.5 }} />
                {!isLaptop && (
                  <Typography variant="cap12Bold" color={text.white} sx={{ opacity: 0.7, px: spacing.sm }}>
                    {section.label}
                  </Typography>
                )}
              </>
            )}
            {section.items.map(renderItem)}
          </Stack>
        ))}
      </ListWrapper>
    );
  }, [pathname, isLaptop, sections]);

  if (!isLaptop) return DrawerContent;
  return (
    <Drawer
      open={isOpenDrawer}
      onClose={toggleDrawer}
      hideBackdrop
      ModalProps={{ sx: { width: "fit-content" } }}
      PaperProps={{ sx: { bgcolor: background.main, height: "calc(100vh - 56px)", top: "56px" } }}
    >
      {/* <IconCloseWrapper>
        <IconCloseBtn onClick={toggleDrawer} />
      </IconCloseWrapper> */}
      {DrawerContent}
    </Drawer>
  );
};

const ListWrapper = styled(Stack)<{ isLaptop: boolean }>`
  padding: ${spacing.md} ${spacing.sm};
  width: ${({ isLaptop }) => (isLaptop ? "80px" : "auto")};
  min-width: ${({ isLaptop }) => (isLaptop ? "auto" : "242px")};
  background-color: ${background.main};
  color: ${text.white};
  min-height: calc(100vh - 57px);
  height: 100%;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 57px;
  z-index: ${zIndex.drawer};
`;

const ListItemWrapper = styled(Box)<{ isLaptop: boolean }>`
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-radius: ${rounded.sm};
  padding: ${({ isLaptop }) => (isLaptop ? `${spacing.xs} 0px` : `${spacing.xs} ${spacing.sm}`)};
  &.active {
    background-color: ${hexToRgba(background.white, 0.3)} !important;
  }

  &:hover {
    background-color: ${hexToRgba(background.white, 0.1)};
  }
`;
